/**
 * admin-server HTTP 客户端 —— `/api/*` 统一 envelope。
 *
 * 契约：所有响应都是 `{ status, code, data, message? }`：
 *   - 成功（status:true）→ 解出 data 给业务代码，业务拿到的就是原始 T
 *   - 失败（status:false）→ 抛 AdminApiError，由全局 toast 提示用户
 *
 * 鉴权统一走 httpOnly cookie；admin-dashboard 不再从 localStorage 读 token。
 * 401 时自动调用 /api/auth/refresh 重放一次。
 *
 * 错误提示：所有写请求（POST/PUT/PATCH/DELETE）默认 toast 失败信息。SWR 的读
 * 路径也提示一次（按 message 去重，不会被轮询刷屏）。极少数需要本地处理的场景
 * （比如「删除时 404 视为成功」）传 `{ silent: true }` 关掉。
 */
import { toast } from "@novel-isr/ui";

const USER_KEY = "admin_user";

export class AdminApiError extends Error {
  readonly status: number;
  readonly code?: string;
  readonly payload: unknown;

  constructor(message: string, options: { status: number; code?: string; payload: unknown }) {
    super(message);
    this.name = "AdminApiError";
    this.status = options.status;
    this.code = options.code;
    this.payload = options.payload;
  }
}

export interface AdminUser {
  id: string;
  handle: string;
  displayName: string;
  email?: string;
  avatarUrl?: string | null;
  /** admin 角色但还没绑 2FA —— 服务端会在 /api/admin/* 返 403 two_factor_required；
   *  顶栏显示强制 banner，引导去 /account 完成 TOTP 绑定。 */
  requiresTwoFactorEnroll?: boolean;
}

export interface AdminAuthSuccess {
  user: AdminUser;
  tokens: {
    accessToken: string;
    refreshToken: string;
    expiresInSec: number;
  };
}

export interface AdminTwoFactorChallenge {
  requiresTwoFactor: true;
  challengeToken: string;
  expiresInSec: number;
  methods: Array<"totp">;
  user: Pick<AdminUser, "id" | "handle" | "displayName" | "avatarUrl">;
}

export function getAdminUser(): AdminUser | null {
  try {
    const raw = window.localStorage.getItem(USER_KEY);
    return raw ? (JSON.parse(raw) as AdminUser) : null;
  } catch {
    return null;
  }
}

export function setAdminSession(input: AdminAuthSuccess): void {
  window.localStorage.setItem(USER_KEY, JSON.stringify(input.user));
}

export function clearAdminSession(): void {
  window.localStorage.removeItem(USER_KEY);
}

export function hasAdminSessionMarker(): boolean {
  try {
    return Boolean(window.localStorage.getItem(USER_KEY));
  } catch {
    return false;
  }
}

export interface ApiEnvelope<T = unknown> {
  status: boolean;
  code: number;
  data: T | null;
  message?: string;
}

interface RequestOptions {
  /** 关闭自动 toast。
   * - `true`：所有错误都静默，调用方自行处理
   * - `(err) => boolean`：只在 predicate 返回 true 时静默；常用于 `silent:
   *   (err) => err instanceof AdminApiError && err.status === 404` 这种「404 视为
   *   已不存在」的场景，其它错误仍走全局 toast */
  silent?: boolean | ((err: unknown) => boolean);
}

export async function adminFetch(input: RequestInfo | URL, init: RequestInit = {}) {
  const headers = new Headers(init.headers);
  if (init.body !== undefined && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  // session 已经判死刑了就别再发了 —— SWR 周期性轮询 / 多组件并发请求会一次次撞
  // 401，每次都触发新 refresh，每次都失败，每次都尝试 redirect（被 sessionExpiredHandled
  // 拦掉），但 console 仍是一墙 401 spam。直接拿一份"已经过期"的 401 response 给调用方，
  // 让 SWR 走 onError 自然收敛。location.assign('/login') 跑完就 reload 了，flag 自然清零。
  if (sessionExpiredHandled && !isAuthEndpoint(input)) {
    return new Response(JSON.stringify({ error: "session_expired" }), {
      status: 401,
      headers: { "content-type": "application/json" },
    });
  }

  let response = await fetch(input, { ...init, headers, credentials: "include" });

  // 401 走"重 refresh + 重放"链路。auth 自身的端点不参与：它们 401 已经是终态
  // （登录失败、challenge 不对），再调 refresh 没意义还会触发死循环。
  if (response.status === 401 && !isAuthEndpoint(input)) {
    const refreshed = await tryRefreshOnce();
    if (refreshed) {
      response = await fetch(input, { ...init, headers, credentials: "include" });
    } else {
      // access + refresh 都拿不到新 cookie → 当前 session 真的死了。
      // 清 localStorage marker，跳登录页；下面 throw 出去的 401 会被全局
      // toast 吃掉，但用户视觉焦点已经落在登录页了。
      handleSessionExpired();
    }
  }
  return response;
}

function isAuthEndpoint(input: RequestInfo | URL): boolean {
  const url = typeof input === "string" ? input : input.toString();
  return (
    url.includes("/api/auth/login") ||
    url.includes("/api/auth/refresh") ||
    url.includes("/api/auth/2fa") ||
    url.includes("/api/auth/logout")
  );
}

/**
 * 多个并发请求同时撞 401 时，只允许一条 refresh 在飞 —— 其它请求复用同一个
 * promise，避免给后端打 N 次冗余 refresh（每次都会写 set-cookie，连环
 * 副作用）。promise resolve 后下一批新请求会启新的 refresh，自然回归。
 */
let refreshInFlight: Promise<boolean> | null = null;
async function tryRefreshOnce(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    try {
      const r = await fetch("/api/auth/refresh", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({}),
      });
      return r.ok;
    } catch {
      return false;
    } finally {
      // 注意：finally 里清空 ref 的时机比 await 出去的调用方拿到结果稍晚没关系
      // —— 调用方在第一时间 await 的就是这个 promise，能拿到正确返回值；新的
      // 调用方（这条 promise 已经 resolve 之后才进入）应该启新的 refresh。
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

/**
 * 会话已经"开始过期"的标记。一旦 true：
 *   - handleSessionExpired 不再重复跳登录
 *   - notifyApiError 静默后续 401（用户已经在跳登录了，弹一墙 toast 没意义）
 *
 * 两条路径会把它置 true：
 *   1. adminFetch 自己撞 401 + refresh 也 401 → handleSessionExpired()
 *   2. 业务代码主动 logout / 改密码后调 endSessionSilently()
 *      —— 跟 1 的区别是不弹"登录已过期" toast、不再次调度 setTimeout
 *      （调用方负责自己的成功 toast + 自己安排跳转节奏）
 *
 * reload 之后自然清零。
 */
let sessionExpiredHandled = false;

function handleSessionExpired(): void {
  if (sessionExpiredHandled) return;
  sessionExpiredHandled = true;
  clearAdminSession();
  // 已经在登录页（比如 LoginPage 里某个 SWR 调用顺手撞 401）就不再跳，
  // 避免反复 reload 导致的页面闪烁
  if (typeof window !== "undefined" && window.location.pathname !== "/login") {
    toast.warning("登录已过期", {
      id: "session-expired",
      description: "正在返回登录页...",
    });
    // 给 toast 一个露脸时间再跳；用 location.assign 做硬跳，把所有 in-memory
    // SWR cache / React state 一并清掉，避免登录新身份后看到旧用户的脏数据
    setTimeout(() => window.location.assign("/login"), 600);
  }
}

/**
 * 业务代码主动告知"会话即将结束（用户自己改密 / 主动登出 / 等等），
 * 接下来 SWR 周期性轮询撞 401 不要再弹 toast 了"。
 *
 * 跟 handleSessionExpired() 的区别：
 *   - 不弹 "登录已过期" toast（调用方有自己的成功 toast）
 *   - 不调度 location.assign（调用方有自己的跳转节奏）
 *   - 只是把 sessionExpiredHandled 置 true，让 notifyApiError 闭嘴
 */
export function endSessionSilently(): void {
  sessionExpiredHandled = true;
}

export async function adminJson<T>(url: string, opts?: RequestOptions): Promise<T> {
  return runWithToast(opts, async () => {
    const response = await adminFetch(url);
    return parseJsonResponse<T>(response, url);
  });
}

export async function adminPostJson<T>(
  url: string,
  body: unknown = {},
  opts?: RequestOptions,
): Promise<T> {
  return runWithToast(opts, async () => {
    const response = await adminFetch(url, { method: "POST", body: JSON.stringify(body) });
    return parseJsonResponse<T>(response, url);
  });
}

export async function adminPutJson<T>(
  url: string,
  body: unknown,
  opts?: RequestOptions,
): Promise<T> {
  return runWithToast(opts, async () => {
    const response = await adminFetch(url, { method: "PUT", body: JSON.stringify(body) });
    return parseJsonResponse<T>(response, url);
  });
}

export async function adminPatchJson<T>(
  url: string,
  body: unknown = {},
  opts?: RequestOptions,
): Promise<T> {
  return runWithToast(opts, async () => {
    const response = await adminFetch(url, { method: "PATCH", body: JSON.stringify(body) });
    return parseJsonResponse<T>(response, url);
  });
}

export async function adminDeleteJson<T>(
  url: string,
  body?: unknown,
  opts?: RequestOptions,
): Promise<T> {
  return runWithToast(opts, async () => {
    const response = await adminFetch(url, {
      method: "DELETE",
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    return parseJsonResponse<T>(response, url);
  });
}

export const adminApi = {
  request: adminFetch,
  get: adminJson,
  post: adminPostJson,
  put: adminPutJson,
  patch: adminPatchJson,
  delete: adminDeleteJson,
};

/** 常用的「容忍某个错误」predicate，可直接传给 RequestOptions.silent。 */
export const tolerate = {
  /** 404：通常用于「删除时目标已不存在」当作成功处理 */
  notFound: (err: unknown): boolean =>
    err instanceof AdminApiError && err.status === 404,
};

export async function loginAdmin(
  input: { identifier: string; password: string },
  opts?: RequestOptions,
): Promise<AdminAuthSuccess | AdminTwoFactorChallenge> {
  return runWithToast(opts, () => postAuth("/api/auth/login", input));
}

export async function verifyAdminTwoFactor(
  input: { challengeToken: string; code: string },
  opts?: RequestOptions,
): Promise<AdminAuthSuccess> {
  return runWithToast(opts, () =>
    postAuth("/api/auth/2fa/verify", input) as Promise<AdminAuthSuccess>,
  );
}

/** 用恢复码代替 TOTP 通过 2FA challenge —— 丢手机 / Authenticator 应急路径 */
export async function verifyAdminTwoFactorRecovery(
  input: { challengeToken: string; recoveryCode: string },
  opts?: RequestOptions,
): Promise<AdminAuthSuccess & { remainingRecoveryCodes: number }> {
  return runWithToast(
    opts,
    () =>
      postAuth("/api/auth/2fa/recovery", input) as Promise<
        AdminAuthSuccess & { remainingRecoveryCodes: number }
      >,
  );
}

export async function logoutAdmin(): Promise<void> {
  // 进入"会话即将结束"状态：即使后台 SWR 在 fetch 飞行中、回来时撞 401，
  // notifyApiError 也会闭嘴，避免登出过程中弹一墙 token-revoked toast。
  endSessionSilently();
  await fetch("/api/auth/logout", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({}),
  }).catch(() => undefined);
  clearAdminSession();
}

async function postAuth<T>(url: string, body: unknown): Promise<T> {
  const response = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(body),
  });
  return parseJsonResponse<T>(response, url);
}

async function runWithToast<T>(
  opts: RequestOptions | undefined,
  task: () => Promise<T>,
): Promise<T> {
  try {
    return await task();
  } catch (err) {
    const skip =
      opts?.silent === true ||
      (typeof opts?.silent === "function" && opts.silent(err));
    if (!skip) notifyApiError(err);
    throw err;
  }
}

/**
 * 把任意来自 admin-api 的错误统一弹成 toast.error。
 *
 * 用 message 文本作为 dedupe id，避免轮询失败 / 多次重试时把同一条错误叠成
 * 一墙：sonner 风格的 toast 按 id 合并。AdminApiError 的 message 来自 envelope
 * 的 message 字段，已是中文业务原因；其它 Error 退回 .message；都没有就给个
 * 兜底「请求失败」。
 *
 * sessionExpiredHandled 期间静默：用户已经在被跳到登录页的路上了，再弹
 * "token revoked" / "missing bearer token" / "token expired" 这类登出连锁
 * 错误只会污染屏幕。
 */
function notifyApiError(err: unknown): void {
  if (sessionExpiredHandled) return;
  const message =
    err instanceof AdminApiError
      ? (err.message ?? `请求失败 (${err.status})`)
      : err instanceof Error
        ? (err.message ?? "请求失败")
        : "请求失败";
  toast.error(message, { id: `api-error:${message}` });
}

/**
 * envelope 契约只覆盖 `/api/*`（跟服务端 api-envelope 插件的 shouldWrap 同口径）。
 * 非 api 路径（`/health/*` / 静态文件）保持 raw shape，按 HTTP 语义判成功。
 */
function shouldExpectEnvelope(url: string): boolean {
  // 兼容相对路径 `/api/foo` 和绝对 URL `https://host/api/foo`
  return url.includes("/api/") || url.endsWith("/api");
}

async function parseJsonResponse<T>(response: Response, url: string): Promise<T> {
  // 204 No Content：DELETE 成功的标准响应，按 RFC 不带 body。服务端的 api-envelope
  // 插件也对 204 显式跳过包装（你不能给空 body 套 envelope）—— 客户端这里跟着对齐，
  // 直接当成功 + 空 payload。注意：HEAD 也是无 body 状态，顺手覆盖。
  if (response.status === 204 || response.status === 205 || response.status === 304) {
    return undefined as T;
  }

  const payload = await readJsonPayload(response);

  // 非 envelope 路径（健康检查等）：直接按 HTTP status 处理 raw payload
  if (!shouldExpectEnvelope(url)) {
    if (!response.ok) {
      const objectPayload = isRecord(payload) ? payload : {};
      throw new AdminApiError(
        typeof objectPayload.message === "string"
          ? objectPayload.message
          : `request failed: ${response.status}`,
        {
          status: response.status,
          code: typeof objectPayload.error === "string" ? objectPayload.error : undefined,
          payload,
        },
      );
    }
    return payload as T;
  }

  if (!isApiEnvelope(payload)) {
    throw new AdminApiError(
      `unexpected response shape (HTTP ${response.status}); expected envelope`,
      {
        status: response.status,
        code: "envelope_missing",
        payload,
      },
    );
  }
  if (payload.status === true) return payload.data as T;
  throw new AdminApiError(payload.message ?? `request failed: ${response.status}`, {
    status: response.status,
    code: String(payload.code),
    payload,
  });
}

function isApiEnvelope(value: unknown): value is ApiEnvelope {
  if (!isRecord(value)) return false;
  return typeof value.status === "boolean" && typeof value.code === "number" && "data" in value;
}

async function readJsonPayload(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return {};
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return { message: text };
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}
